import { DraggableProvided } from '@hello-pangea/dnd'
import { RankingItem } from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import { useMemo } from 'react'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { Block } from 'services/Store/Project/types'

import type { TestRankingElementType } from '../testRankingTypes'
import * as s from './TestRankingElement.module.scss'
import TestRankingItem from './TestRankingItem'

interface ITestRankingElementPreviewItemProps
  extends Pick<
    React.ComponentProps<TestRankingElementType>,
    'element' | 'mode' | 'styles' | 'waiting' | 'onChange' | 'font' | 'feedback'
  > {
  index: number
  provided: DraggableProvided
  showControls?: boolean
  isDragging: boolean
  rankingItem: RankingItem
  block?: Block | null
}

const TestRankingElementPreviewItem: React.FC<ITestRankingElementPreviewItemProps> = ({
  provided,
  index,
  isDragging,
  element,
  mode,
  styles,
  block,
  font,
  feedback,
  rankingItem,
}) => {
  const { value } = element
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const feedbackValue = getFeedbackValue(value)

  const isViewMode = mode.previewMode !== PreviewMode.editor
  const isDisabled = Boolean(blockResult || !isViewMode)
  // remember first values as right order (maybe better use for it block.test.successRules)
  const validOrderMap = useMemo(
    () =>
      (value?.items || []).reduce(
        (result, item, index) => {
          result[item.value || ''] = index
          return result
        },
        {} as { [id: string]: number },
      ),
    [value?.items],
  )

  return (
    <li
      className={s.wrapItem}
      ref={provided.innerRef}
      {...provided.draggableProps}
      aria-describedby={undefined}
      aria-posinset={index + 1}
      aria-setsize={value.items.length}
      style={{
        ...provided.draggableProps.style,
        marginBottom: styles.offset.marginTop,
      }}
    >
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={isDisabled && validOrderMap[rankingItem.value || ''] === index}
        label={`${rtValueToText(rankingItem.label)}`}
        showAnswerFb={value.answerFeedback}
        styles={feedback}
      >
        <TestRankingItem
          dragHandleProps={provided.dragHandleProps}
          font={font}
          index={index}
          isDragging={isDragging}
          label={rtValueToText(rankingItem.label)}
          styles={styles}
        >
          <div className={s.text}>
            <RichTextViewer styles={font} value={rankingItem.label || textToRtValue('')} />
          </div>
        </TestRankingItem>
      </TestFeedback>
    </li>
  )
}

export default TestRankingElementPreviewItem
